// iter-121.117 — OA Cockpit Scope Context
//
// Sibling of `cockpit-scope.jsx` (SA cockpit). The OA cockpit is always
// pinned to the OA's own org server-side, so the context only exposes
// Tenant / Workspace / User filters (no org picker).
//
// Widgets below the OaScopeBar consume via `useOaScope()` and pass
// `queryString` directly into their fetch URLs.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const OaScopeContext = createContext(null);

const DEFAULT_SCOPE = {
    period: "30d",
    custom_start: null,
    custom_end: null,
    tenant_id: null,
    workspace_id: null,
    owner_id: null,
};

export function OaScopeProvider({ children }) {
    const [scope, setScope] = useState(DEFAULT_SCOPE);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [options, setOptions] = useState({ tenants: [], workspaces: [], owners: [] });
    const [optionsLoading, setOptionsLoading] = useState(false);

    // Load saved default scope on mount.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get("/home/oa/scope/preferences");
                if (!alive) return;
                const d = r.data || {};
                setScope({
                    period: d.period || "30d",
                    custom_start: d.custom_start || null,
                    custom_end: d.custom_end || null,
                    tenant_id: d.tenant_id || null,
                    workspace_id: d.workspace_id || null,
                    owner_id: d.owner_id || null,
                });
            } catch {
                // 403 (non-OA) or 5xx — fall back to defaults silently.
            } finally {
                if (alive) setLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, []);

    // Refetch scope-options whenever period or tenant_id changes — owners
    // + workspaces cascade from those.
    useEffect(() => {
        if (!loaded) return;
        (async () => {
            setOptionsLoading(true);
            try {
                const params = new URLSearchParams({ period: scope.period });
                if (scope.custom_start) params.set("custom_start", scope.custom_start);
                if (scope.custom_end)   params.set("custom_end",   scope.custom_end);
                if (scope.tenant_id) params.set("tenant_id", scope.tenant_id);
                const r = await api.get(`/home/oa/scope/options?${params}`);
                setOptions({
                    tenants: r.data?.tenants || [],
                    workspaces: r.data?.workspaces || [],
                    owners: r.data?.owners || [],
                });
            } catch {
                setOptions({ tenants: [], workspaces: [], owners: [] });
            } finally {
                setOptionsLoading(false);
            }
        })();
    }, [scope.period, scope.custom_start, scope.custom_end, scope.tenant_id, loaded]);

    // Patch helpers — null/empty string clears the field.
    const setPeriod = useCallback((period) => {
        setScope((s) => ({
            ...s,
            period: period || "30d",
            custom_start: period === "custom" ? s.custom_start : null,
            custom_end:   period === "custom" ? s.custom_end   : null,
        }));
    }, []);

    const setCustomRange = useCallback((start, end) => {
        setScope((s) => ({
            ...s,
            period: "custom",
            custom_start: start || null,
            custom_end: end || null,
        }));
    }, []);

    const setTenantId = useCallback((tenant_id) => {
        // Switching tenant clears workspace + owner (they may have belonged to the prior tenant).
        setScope((s) => ({
            ...s,
            tenant_id: tenant_id || null,
            workspace_id: null,
            owner_id: null,
        }));
    }, []);

    const setWorkspaceId = useCallback((workspace_id) => {
        setScope((s) => ({ ...s, workspace_id: workspace_id || null }));
    }, []);

    const setOwnerId = useCallback((owner_id) => {
        setScope((s) => ({ ...s, owner_id: owner_id || null }));
    }, []);

    const reset = useCallback(() => setScope(DEFAULT_SCOPE), []);

    const saveAsDefault = useCallback(async () => {
        setSaving(true);
        try {
            await api.put("/home/oa/scope/preferences", scope);
            return true;
        } catch {
            return false;
        } finally {
            setSaving(false);
        }
    }, [scope]);

    const queryString = useMemo(() => {
        const p = new URLSearchParams({ period: scope.period });
        if (scope.custom_start) p.set("custom_start", scope.custom_start);
        if (scope.custom_end)   p.set("custom_end",   scope.custom_end);
        if (scope.tenant_id)    p.set("tenant_id",    scope.tenant_id);
        if (scope.workspace_id) p.set("workspace_id", scope.workspace_id);
        if (scope.owner_id)     p.set("owner_id",     scope.owner_id);
        return p.toString();
    }, [scope]);

    const value = useMemo(() => ({
        scope, options, optionsLoading, loaded, saving,
        setPeriod, setCustomRange, setTenantId, setWorkspaceId, setOwnerId,
        reset, saveAsDefault, queryString,
    }), [scope, options, optionsLoading, loaded, saving,
        setPeriod, setCustomRange, setTenantId, setWorkspaceId, setOwnerId,
        reset, saveAsDefault, queryString]);

    return (
        <OaScopeContext.Provider value={value}>
            {children}
        </OaScopeContext.Provider>
    );
}

export function useOaScope() {
    const ctx = useContext(OaScopeContext);
    if (!ctx) {
        throw new Error("useOaScope must be used inside <OaScopeProvider>");
    }
    return ctx;
}
